import { defineStore } from 'pinia'
import type { RouteLocationNormalized } from 'vue-router'

// The Account ("Mig") page is no longer a bottom-nav tab — it is reached from
// the header initials, from any page. Its back button therefore has to return
// to wherever the user came from rather than to one fixed route. Browser
// history can't answer that: Orders → Mig → Profil → (back to Mig) → "back"
// would land on Profil again. So the page the user entered Mig from is kept
// here, and Mig's own sub-pages (which just go back to Mig) never overwrite it.
//
// Not cleared on logout: every entry to Account from a normal page overwrites
// it, and a hard load of Account clears it, so a stale value can't outlive
// the navigation that would have replaced it.

// Pages that can never be where the user "came from" for Account: Account's
// own sub-pages (that would loop), and the full-screen guided flows.
const NON_RETURN_PATH_PREFIXES = ['/app/account', '/app/app-lock', '/app/about', '/app/terms-of-service']

function isReturnableOrigin(path: string): boolean {
  if (!path.startsWith('/app')) return false
  return !NON_RETURN_PATH_PREFIXES.some((x) => path.startsWith(x))
}

export const useAccountReturnStore = defineStore('accountReturn', {
  state: () => ({
    returnTo: null as string | null,
  }),

  actions: {
    // Called from the router's afterEach — only Account itself is of interest.
    recordNavigation(
      to: Pick<RouteLocationNormalized, 'name'>,
      from: Pick<RouteLocationNormalized, 'name' | 'path' | 'fullPath'>
    ): void {
      if (to.name !== 'Account') return

      // First navigation of the session (hard load / refresh on Account):
      // there is nowhere "before" to return to.
      if (!from.name) {
        this.returnTo = null
        return
      }

      if (isReturnableOrigin(from.path)) {
        this.returnTo = from.fullPath
      }
    },
  },
})
